/**
 * Header (Menu 2.0) — <ej-header> custom element.
 * Desktop megamenus, the sliding nav underline, the search panel, the
 * condensed sticky flag, and the mobile menu (accordion or push subpanels).
 * The desktop/mobile switch is a Liquid-computed media query in
 * sections/header.liquid; this element only mirrors it onto data-layout.
 */
(function () {
  'use strict';

  if (customElements.get('ej-header')) return;

  var MEGA_MOTION = 300;
  var MEGA_CLOSE_MOTION = 180;
  var CLOSE_DELAY = 90;
  var AIM_MAX = 300;
  var MOBILE_MOTION = 300;
  var SUB_MOTION = 250;
  var ARC_MOTION = 200;
  // Matches the max-height transition on .ej-header__acc-panel
  var ACC_MOTION = 420;
  var ACC_SETTLE = 60;
  // Separate thresholds: condensing folds ~45px back into scrollY
  var CONDENSE_AT = 128;
  var EXPAND_AT = 40;
  var CONDENSE_SETTLE = 460;

  var cubicBezier = function (x1, y1, x2, y2) {
    var at = function (t, a, b) {
      return ((1 - 3 * b + 3 * a) * t + (3 * b - 6 * a)) * t * t + 3 * a * t;
    };
    var slope = function (t, a, b) {
      return (3 * (1 - 3 * b + 3 * a) * t + 2 * (3 * b - 6 * a)) * t + 3 * a;
    };
    return function (x) {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      var t = x;
      for (var i = 0; i < 8; i++) {
        var dx = at(t, x1, x2) - x;
        var d = slope(t, x1, x2);
        if (Math.abs(dx) < 0.0001 || d === 0) break;
        t -= dx / d;
      }
      return at(t, y1, y2);
    };
  };

  class EJHeader extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      var signal = this.abortController.signal;
      var root = this;
      var listen = function (target, type, fn, opts) {
        if (!target) return;
        target.addEventListener(type, fn, Object.assign({}, opts || {}, { signal: signal }));
      };

      var reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      var motionDisabled = function () {
        return reducedMotionQuery.matches || document.body.hasAttribute('data-disable-animations');
      };
      var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

      var timers = new Set();
      this._timers = timers;
      var later = function (fn, delay) {
        var t = setTimeout(function () {
          timers.delete(t);
          fn();
        }, delay);
        timers.add(t);
        return t;
      };
      var clearTimer = function (t) {
        if (t) {
          clearTimeout(t);
          timers.delete(t);
        }
      };

      var stickyEnabled = root.getAttribute('data-sticky') !== 'never';

      /* ---------- desktop mega menus ---------- */
      var desktopWrap = root.querySelector('[data-desktop-wrap]');
      var desktopHeader = root.querySelector('[data-desktop-header]');
      var megaTriggers = Array.prototype.slice.call(root.querySelectorAll('[data-mega-trigger]'));
      var hoverTargets = Array.prototype.slice.call(root.querySelectorAll('[data-mega-hover-trigger]'));
      var panels = Array.prototype.slice.call(root.querySelectorAll('[data-menu-panel]'));
      var panelTimers = new WeakMap();
      var activeMega = null;
      var activeTrigger = null;
      var activeHoverTarget = null;
      var aimTriangle = null;
      var aimTimer = 0;
      var pendingTrigger = null;
      var pointerTrail = [];
      var lastPointerTarget = null;

      panels.forEach(function (p) {
        p.dataset.state = 'closed';
        p.inert = true;
        p.hidden = true;
        Array.prototype.forEach.call(p.querySelectorAll('[data-appear]'), function (el, i) {
          el.style.setProperty('--appear-delay', 60 + i * 60 + 'ms');
        });
      });

      /* Shared sliding underline between top-level nav items */
      var navList = root.querySelector('[data-desktop-nav-list]');
      var arc = {
        controls: navList
          ? Array.prototype.slice.call(navList.querySelectorAll('[data-nav-item] > a'))
          : [],
        lastTarget: null,
        visible: false,
        hiddenAt: 0,
        revealFrame: 0,
        frame: 0
      };
      var preferredArcTarget = function () {
        var expanded = arc.controls.find(function (c) {
          var item = c.closest('[data-nav-item]');
          return item && item.getAttribute('data-expanded') === 'true';
        });
        if (expanded) return expanded;
        var focused = arc.controls.find(function (c) {
          var item = c.closest('[data-nav-item]');
          return item && item.matches(':focus-within');
        });
        if (focused) return focused;
        if (!finePointer.matches) return null;
        return (
          arc.controls.find(function (c) {
            return c.matches(':hover');
          }) || null
        );
      };
      var positionArc = function (target, visible) {
        if (!navList) return;
        if (!target || !target.getClientRects().length) {
          cancelAnimationFrame(arc.revealFrame);
          arc.revealFrame = 0;
          arc.visible = false;
          arc.hiddenAt = 0;
          navList.style.setProperty('--header-arc-reveal-inset', '50%');
          return;
        }
        var ownerBounds = navList.getBoundingClientRect();
        var targetBounds = target.getBoundingClientRect();
        navList.style.setProperty('--header-arc-color', target.dataset.arcColor || '');
        var inlineStart = targetBounds.left - ownerBounds.left + 16;
        var width = Math.max(0, targetBounds.width - 32);
        cancelAnimationFrame(arc.revealFrame);
        arc.revealFrame = 0;
        if (!visible) {
          arc.lastTarget = target;
          if (arc.visible) arc.hiddenAt = performance.now();
          arc.visible = false;
          navList.style.setProperty('--header-arc-inline-start', inlineStart + 'px');
          navList.style.setProperty('--header-arc-width', width + 'px');
          navList.style.setProperty('--header-arc-reveal-inset', '50%');
          return;
        }
        if (!arc.visible && performance.now() - arc.hiddenAt >= ARC_MOTION) {
          arc.lastTarget = target;
          arc.visible = true;
          navList.dataset.headerArcSnapping = 'true';
          navList.style.setProperty('--header-arc-inline-start', inlineStart + 'px');
          navList.style.setProperty('--header-arc-width', width + 'px');
          navList.style.setProperty('--header-arc-reveal-inset', '50%');
          navList.getBoundingClientRect();
          delete navList.dataset.headerArcSnapping;
          arc.revealFrame = requestAnimationFrame(function () {
            arc.revealFrame = 0;
            if (preferredArcTarget() !== target) {
              arc.visible = false;
              scheduleArcs();
              return;
            }
            navList.style.setProperty('--header-arc-reveal-inset', '0px');
          });
          return;
        }
        arc.lastTarget = target;
        arc.visible = true;
        navList.style.setProperty('--header-arc-inline-start', inlineStart + 'px');
        navList.style.setProperty('--header-arc-width', width + 'px');
        navList.style.setProperty('--header-arc-reveal-inset', '0px');
      };
      var syncArcs = function () {
        arc.frame = 0;
        var preferred = preferredArcTarget();
        var target = preferred || arc.lastTarget || arc.controls[0];
        positionArc(target, Boolean(preferred));
      };
      var scheduleArcs = function () {
        if (!arc.frame) arc.frame = requestAnimationFrame(syncArcs);
      };
      arc.controls.forEach(function (c) {
        listen(c, 'pointerenter', function (e) {
          if (e.pointerType === 'mouse' && finePointer.matches) scheduleArcs();
        });
        listen(c, 'focusin', scheduleArcs);
      });
      if (navList) {
        listen(navList, 'pointerleave', function (e) {
          if (e.pointerType === 'mouse' && finePointer.matches) scheduleArcs();
        });
        listen(navList, 'focusout', function () {
          queueMicrotask(scheduleArcs);
        });
      }

      var clearPanelTimer = function (p) {
        var t = panelTimers.get(p);
        if (t) {
          clearTimer(t);
          panelTimers.delete(p);
        }
      };
      var showPanel = function (p, opts) {
        clearPanelTimer(p);
        var wasHidden = p.hidden;
        p.hidden = false;
        p.inert = false;
        p.toggleAttribute('data-switching', Boolean(opts && opts.switching));
        if (motionDisabled()) {
          p.dataset.state = 'open';
          return;
        }
        if (wasHidden) {
          p.dataset.state = 'closed';
          p.getBoundingClientRect();
        }
        p.dataset.state = 'opening';
        panelTimers.set(
          p,
          later(function () {
            if (p.dataset.state === 'opening') p.dataset.state = 'open';
            p.style.height = '';
            panelTimers.delete(p);
          }, MEGA_MOTION)
        );
      };
      var hidePanel = function (p, opts) {
        clearPanelTimer(p);
        p.inert = true;
        var switching = Boolean(opts && opts.switching);
        p.toggleAttribute('data-switching', switching);
        if (motionDisabled()) {
          p.dataset.state = 'closed';
          p.hidden = true;
          p.style.height = '';
          return;
        }
        var skipHold = switching || (opts && opts.skipCloseHold);
        var hold = skipHold || p.dataset.state === 'opening' ? 0 : CLOSE_DELAY;
        p.style.setProperty('--panel-close-hold', hold + 'ms');
        p.dataset.state = 'closing';
        panelTimers.set(
          p,
          later(function () {
            p.dataset.state = 'closed';
            p.hidden = true;
            p.style.height = '';
            panelTimers.delete(p);
          }, hold + MEGA_CLOSE_MOTION)
        );
      };
      var morphPanelHeight = function (p, fromHeight) {
        if (motionDisabled()) return;
        var morphing = panels.filter(function (o) {
          return o === p || (o.dataset.state === 'closing' && o.hasAttribute('data-switching'));
        });
        p.style.height = '';
        var toHeight = p.getBoundingClientRect().height;
        morphing.forEach(function (o) {
          o.style.height = fromHeight + 'px';
        });
        p.getBoundingClientRect();
        morphing.forEach(function (o) {
          o.style.height = toHeight + 'px';
        });
      };
      var setTriggerLabel = function (trigger, expanded) {
        if (!trigger) return;
        var label = expanded ? trigger.dataset.labelClose : trigger.dataset.labelOpen;
        if (label) trigger.setAttribute('aria-label', label);
      };
      var setExpandedAttrs = function (trigger, expanded) {
        if (!trigger) return;
        trigger.setAttribute('aria-expanded', String(expanded));
        var item = trigger.closest('[data-nav-item]');
        if (item) item.setAttribute('data-expanded', String(expanded));
        if (trigger.hasAttribute('data-mega-trigger')) setTriggerLabel(trigger, expanded);
        scheduleArcs();
      };
      var cancelAim = function () {
        clearTimer(aimTimer);
        aimTimer = 0;
        aimTriangle = null;
        pendingTrigger = null;
      };
      var megaContains = function (node) {
        return (
          node instanceof Node &&
          Boolean(
            (activeMega && activeMega.contains(node)) ||
              (activeHoverTarget && activeHoverTarget.contains(node))
          )
        );
      };
      var megaHasFocus = function () {
        return megaContains(document.activeElement);
      };
      var visibleControls = function (p) {
        if (!p) return [];
        return Array.prototype.filter.call(
          p.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])'),
          function (c) {
            return c.getClientRects().length > 0;
          }
        );
      };
      var triggerForNode = function (node) {
        if (!(node instanceof Element)) return null;
        var direct = node.closest('[data-mega-trigger]');
        if (direct) return direct;
        var hover = node.closest('[data-mega-hover-trigger]');
        return hover ? hover.querySelector('[data-mega-trigger]') : null;
      };
      var closeMega = function (opts) {
        opts = opts || {};
        cancelAim();
        if (!activeMega) return;
        var p = activeMega;
        var t = activeTrigger;
        hidePanel(p, { skipCloseHold: opts.skipCloseHold, switching: opts.switching });
        setExpandedAttrs(t, false);
        activeMega = activeTrigger = activeHoverTarget = null;
        if (opts.restoreFocus && t) t.focus();
      };
      var closeFromPointer = function () {
        if (!megaHasFocus()) closeMega();
      };
      var openMega = function (trigger, opts) {
        opts = opts || {};
        if (opts.source === 'hover' && megaHasFocus()) return;
        var p = document.getElementById(trigger.getAttribute('data-mega-trigger'));
        if (!p || p === activeMega) return;
        if (searchOpen) closeSearch();
        var outgoing = activeMega;
        var fromHeight = outgoing ? outgoing.getBoundingClientRect().height : 0;
        closeMega({ switching: Boolean(outgoing) });
        activeMega = p;
        activeTrigger = trigger;
        activeHoverTarget = trigger.closest('[data-mega-hover-trigger]') || trigger;
        setExpandedAttrs(trigger, true);
        showPanel(p, { switching: Boolean(outgoing) });
        if (outgoing) morphPanelHeight(p, fromHeight);
      };
      var triangleContains = function (pt, tri) {
        var sa = function (a, b, c) {
          return (a.x - c.x) * (b.y - c.y) - (b.x - c.x) * (a.y - c.y);
        };
        var d1 = sa(pt, tri.apex, tri.left);
        var d2 = sa(pt, tri.left, tri.right);
        var d3 = sa(pt, tri.right, tri.apex);
        return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
      };
      var createAim = function (e) {
        if (!activeMega) return null;
        var cur = { x: e.clientX, y: e.clientY };
        var prev = pointerTrail
          .slice()
          .reverse()
          .find(function (p) {
            return Math.hypot(p.x - cur.x, p.y - cur.y) >= 4;
          });
        if (!prev) return null;
        var dx = cur.x - prev.x;
        var dy = cur.y - prev.y;
        var b = activeMega.getBoundingClientRect();
        var destY = b.top + 12;
        var dist = destY - cur.y;
        if (dy <= 0 || dist <= 0) return null;
        var projX = cur.x + (dx / dy) * dist;
        if (projX < b.left || projX > b.right) return null;
        var half = Math.min(144, Math.max(48, Math.abs(dx) + 48));
        return {
          apex: cur,
          left: { x: Math.max(b.left, projX - half), y: destY },
          right: { x: Math.min(b.right, projX + half), y: destY },
          expiresAt: performance.now() + AIM_MAX
        };
      };
      var resolveAim = function () {
        var queued = pendingTrigger;
        var queuedHover = queued ? queued.closest('[data-mega-hover-trigger]') : null;
        var target = lastPointerTarget;
        cancelAim();
        if (queuedHover && queuedHover.matches(':hover')) {
          openMega(queued, { source: 'hover' });
          return;
        }
        if (!megaContains(target)) closeFromPointer();
      };
      var beginAim = function (e) {
        var tri = createAim(e);
        if (!tri) return false;
        cancelAim();
        aimTriangle = tri;
        aimTimer = later(resolveAim, AIM_MAX);
        return true;
      };
      listen(
        desktopHeader,
        'pointermove',
        function (e) {
          if (e.pointerType !== 'mouse' || !finePointer.matches) return;
          var now = performance.now();
          lastPointerTarget = e.target;
          pointerTrail.push({ x: e.clientX, y: e.clientY, time: now });
          pointerTrail = pointerTrail
            .filter(function (p) {
              return now - p.time <= 160;
            })
            .slice(-6);
          if (aimTriangle) {
            if (activeMega && activeMega.contains(e.target)) {
              cancelAim();
              return;
            }
            if (
              now <= aimTriangle.expiresAt &&
              triangleContains({ x: e.clientX, y: e.clientY }, aimTriangle)
            ) {
              return;
            }
            cancelAim();
          }
          /* The scrim is a descendant of the header, so its pointerleave cannot
             fire while a panel is open; hover-out is decided here instead. */
          if (!activeMega || megaContains(e.target)) return;
          var entered = triggerForNode(e.target);
          if (entered && entered !== activeTrigger) openMega(entered, { source: 'hover' });
          else closeFromPointer();
        },
        { passive: true }
      );
      hoverTargets.forEach(function (ht) {
        var trigger = ht.querySelector('[data-mega-trigger]');
        if (!trigger) return;
        listen(ht, 'pointerenter', function (e) {
          if (e.pointerType !== 'mouse' || !finePointer.matches || megaHasFocus()) return;
          if (
            aimTriangle &&
            activeTrigger !== trigger &&
            triangleContains({ x: e.clientX, y: e.clientY }, aimTriangle)
          ) {
            pendingTrigger = trigger;
            return;
          }
          cancelAim();
          openMega(trigger, { source: 'hover' });
        });
        listen(ht, 'pointerleave', function (e) {
          if (e.pointerType !== 'mouse' || !finePointer.matches) return;
          if (activeHoverTarget !== ht) return;
          var next = e.relatedTarget;
          if (next instanceof Node && activeMega && activeMega.contains(next)) {
            cancelAim();
            return;
          }
          if (beginAim(e)) return;
          var nextTrigger = triggerForNode(next);
          if (nextTrigger && nextTrigger !== trigger) openMega(nextTrigger, { source: 'hover' });
          else closeFromPointer();
        });
      });
      megaTriggers.forEach(function (trigger) {
        listen(trigger, 'click', function (e) {
          e.preventDefault();
          cancelAim();
          if (activeTrigger === trigger) {
            closeMega();
            return;
          }
          openMega(trigger, { source: e.detail === 0 ? 'keyboard' : 'pointer' });
          if (e.detail === 0) {
            var first = visibleControls(activeMega)[0];
            if (first) first.focus();
          }
        });
        var navItem = trigger.closest('[data-nav-item]');
        var pairedLink = navItem ? navItem.querySelector('[data-mega-link]') : null;
        [trigger, pairedLink].forEach(function (el) {
          listen(el, 'keydown', function (e) {
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
            e.preventDefault();
            openMega(trigger, { source: 'keyboard' });
            var cs = visibleControls(activeMega);
            var next = e.key === 'ArrowUp' ? cs[cs.length - 1] : cs[0];
            if (next) next.focus();
          });
        });
      });
      listen(desktopHeader, 'pointerleave', function (e) {
        if (e.pointerType !== 'mouse' || !finePointer.matches) return;
        cancelAim();
        closeFromPointer();
      });
      panels.forEach(function (p) {
        listen(p, 'pointerenter', function (e) {
          if (e.pointerType === 'mouse') cancelAim();
        });
        listen(p, 'keydown', function (e) {
          var cs = visibleControls(p);
          var first = cs[0];
          var last = cs[cs.length - 1];
          if (e.key === 'Escape') {
            e.preventDefault();
            if (p === searchPanel) closeSearch({ restoreFocus: true });
            else closeMega({ restoreFocus: true });
          } else if (e.key === 'Tab' && e.shiftKey && document.activeElement === first && p === activeMega) {
            e.preventDefault();
            if (activeTrigger) activeTrigger.focus();
          } else if (e.key === 'Tab' && !e.shiftKey && document.activeElement === last && p === activeMega) {
            e.preventDefault();
            var top = Array.prototype.filter.call(
              root.querySelectorAll('[data-desktop-header] a[href], [data-desktop-header] button:not([disabled])'),
              function (c) {
                return !c.closest('[data-menu-panel]');
              }
            );
            var i = top.indexOf(activeTrigger);
            closeMega();
            if (top[i + 1]) top[i + 1].focus();
          }
        });
      });
      listen(root, 'focusin', function (e) {
        if (activeMega && !megaContains(e.target)) closeMega();
      });
      listen(document, 'pointerdown', function (e) {
        if (activeMega && !megaContains(e.target)) closeMega();
        if (
          searchOpen &&
          searchPanel &&
          !(searchPanel.contains(e.target) || (searchTrigger && searchTrigger.contains(e.target)))
        ) {
          closeSearch();
        }
      });
      listen(document, 'keydown', function (e) {
        if (e.key !== 'Escape') return;
        if (activeMega) {
          e.preventDefault();
          closeMega({ restoreFocus: true });
        } else if (searchOpen) {
          e.preventDefault();
          closeSearch({ restoreFocus: true });
        }
      });

      /* ---------- desktop search panel ---------- */
      var searchPanel = root.querySelector('[data-search-panel]');
      var searchTrigger = root.querySelector('[data-search-trigger]');
      var searchOpen = false;
      var openSearch = function () {
        if (!searchPanel || searchOpen) return;
        closeMega({ skipCloseHold: true });
        searchOpen = true;
        showPanel(searchPanel);
        if (searchTrigger) searchTrigger.setAttribute('aria-expanded', 'true');
        var input = searchPanel.querySelector('[data-search-input]');
        if (input) input.focus();
      };
      var closeSearch = function (opts) {
        opts = opts || {};
        if (!searchPanel || !searchOpen) return;
        searchOpen = false;
        hidePanel(searchPanel);
        if (searchTrigger) searchTrigger.setAttribute('aria-expanded', 'false');
        if (opts.restoreFocus && searchTrigger) searchTrigger.focus();
      };
      listen(searchTrigger, 'click', function () {
        if (searchOpen) closeSearch({ restoreFocus: true });
        else openSearch();
      });

      /* ---------- condensed sticky state ---------- */
      var condensedResync = 0;
      var syncScrollState = function () {
        if (!stickyEnabled) return;
        var y = window.scrollY || document.documentElement.scrollTop || 0;
        var threshold = root.dataset.condensed === 'true' ? EXPAND_AT : CONDENSE_AT;
        var condensed = String(y > threshold);
        if (root.dataset.condensed === condensed) return;
        root.dataset.condensed = condensed;
        scheduleArcs();
        clearTimer(condensedResync);
        condensedResync = later(function () {
          condensedResync = 0;
          scheduleArcs();
          syncScrollState();
        }, CONDENSE_SETTLE);
      };
      if (stickyEnabled) {
        listen(window, 'scroll', syncScrollState, { passive: true });
        syncScrollState();
      }

      /* ---------- mobile menu ---------- */
      var mobileSection = root.querySelector('[data-mobile-wrap]');
      var mobileMenu = root.querySelector('[data-mobile-menu]');
      var mobileTrigger = root.querySelector('[data-open-mobile-menu]');
      var mobileHeader = root.querySelector('[data-mobile-site-header]');
      var accordions = Array.prototype.slice.call(root.querySelectorAll('[data-mobile-accordion]'));
      var subpanels = Array.prototype.slice.call(root.querySelectorAll('[data-subpanel]'));
      var mobileMain = root.querySelector('[data-mobile-main]');
      var mobileOpener = null;
      var mobileTimer = 0;
      var scrollLocked = false;

      subpanels.forEach(function (sp) {
        sp.inert = true;
      });
      var setMenuState = function (state) {
        if (mobileSection) mobileSection.setAttribute('data-menu-state', state);
      };
      var positionMobileMenu = function () {
        if (!mobileMenu || !mobileHeader) return;
        mobileMenu.style.top = mobileHeader.offsetTop + mobileHeader.offsetHeight + 'px';
        var barBottom = mobileHeader.getBoundingClientRect().bottom;
        mobileMenu.style.height = Math.max(0, window.innerHeight - barBottom) + 'px';
      };
      if (mobileMenu) {
        Array.prototype.forEach.call(mobileMenu.querySelectorAll('[data-appear]'), function (el, i) {
          el.style.setProperty('--appear-delay', 60 + i * 60 + 'ms');
        });
      }

      var mobileBody = root.querySelector('[data-mobile-body]');
      var anchorFrame = 0;
      var cancelAnchor = function () {
        cancelAnimationFrame(anchorFrame);
        anchorFrame = 0;
      };
      // Same curve as --ej-ease so the row and the panels move together; the
      // row is re-read each frame because the sibling collapsing above it
      // keeps moving it
      var accEase = cubicBezier(0.2, 0.06, 0.05, 0.95);
      var anchorRow = function (row) {
        if (!mobileBody) return;
        cancelAnchor();
        var startOffset = row.getBoundingClientRect().top - mobileBody.getBoundingClientRect().top;
        var duration = motionDisabled() ? 0 : ACC_MOTION;
        var start = performance.now();
        var step = function (now) {
          anchorFrame = 0;
          var progress = duration ? Math.min(1, (now - start) / duration) : 1;
          mobileBody.scrollTop = contentTop(row) - startOffset * (1 - accEase(progress));
          if (now - start < duration + ACC_SETTLE) anchorFrame = requestAnimationFrame(step);
        };
        anchorFrame = requestAnimationFrame(step);
      };

      var accTimers = new WeakMap();
      var sizeAccPanel = function (p, expanded) {
        var pending = accTimers.get(p);
        if (pending) {
          clearTimer(pending);
          accTimers.delete(p);
        }
        if (motionDisabled()) {
          p.style.maxHeight = expanded ? 'none' : '';
          return;
        }
        if (expanded) {
          p.style.maxHeight = p.scrollHeight + 'px';
        } else {
          p.style.maxHeight = p.getBoundingClientRect().height + 'px';
          p.getBoundingClientRect();
          p.style.maxHeight = '0px';
        }
        accTimers.set(
          p,
          later(function () {
            accTimers.delete(p);
            p.style.maxHeight = expanded ? 'none' : '';
          }, ACC_MOTION)
        );
      };
      var accExpanded = function (a) {
        var t = a.querySelector('[data-mobile-accordion-trigger]');
        return t ? t.getAttribute('aria-expanded') === 'true' : false;
      };
      var setAcc = function (a, expanded) {
        var t = a.querySelector('[data-mobile-accordion-trigger]');
        var p = a.querySelector('[data-acc-panel]');
        if (!t || !p) return;
        var was = a.getAttribute('data-open') === 'true';
        if (!expanded && p.contains(document.activeElement)) t.focus({ preventScroll: true });
        t.setAttribute('aria-expanded', String(expanded));
        a.setAttribute('data-open', String(expanded));
        var content = p.querySelector('[data-mobile-accordion-panel]');
        if (content) content.inert = !expanded;
        if (was !== expanded) sizeAccPanel(p, expanded);
      };
      var contentTop = function (el) {
        return el.getBoundingClientRect().top - mobileBody.getBoundingClientRect().top + mobileBody.scrollTop;
      };
      accordions.forEach(function (a) {
        setAcc(a, accExpanded(a));
        listen(a.querySelector('[data-mobile-accordion-trigger]'), 'click', function () {
          if (accExpanded(a)) {
            cancelAnchor();
            setAcc(a, false);
            return;
          }
          anchorRow(a);
          accordions.forEach(function (o) {
            if (o !== a) setAcc(o, false);
          });
          setAcc(a, true);
        });
      });

      var openSub = function () {
        return subpanels.find(function (sp) {
          return sp.getAttribute('data-open') === 'true';
        });
      };
      var openSubpanel = function (id, opener) {
        var sp = document.getElementById(id);
        if (!sp) return;
        sp._opener = opener;
        sp.setAttribute('data-open', 'true');
        if (mobileSection) mobileSection.setAttribute('data-sub-open', id);
        sp.inert = false;
        if (mobileMain) mobileMain.inert = true;
        if (opener) opener.setAttribute('aria-expanded', 'true');
        setMenuState('level2');
        later(function () {
          var first =
            sp.querySelector('[data-back-subpanel]') ||
            sp.querySelector('a[href], button:not([disabled])');
          if (first) first.focus({ preventScroll: true });
        }, motionDisabled() ? 0 : SUB_MOTION);
      };
      var closeSubpanel = function (sp, opts) {
        opts = opts || {};
        if (!sp || sp.getAttribute('data-open') !== 'true') return;
        sp.setAttribute('data-open', 'false');
        if (mobileSection) mobileSection.removeAttribute('data-sub-open');
        sp.inert = true;
        if (mobileMain) mobileMain.inert = false;
        if (sp._opener) sp._opener.setAttribute('aria-expanded', 'false');
        if (isMobileOpen()) setMenuState('level1');
        if (opts.restoreFocus !== false && sp._opener) sp._opener.focus({ preventScroll: true });
      };
      Array.prototype.forEach.call(root.querySelectorAll('[data-open-subpanel]'), function (btn) {
        listen(btn, 'click', function () {
          openSubpanel(btn.getAttribute('data-open-subpanel'), btn);
        });
      });
      Array.prototype.forEach.call(root.querySelectorAll('[data-back-subpanel]'), function (btn) {
        listen(btn, 'click', function () {
          closeSubpanel(openSub());
        });
      });

      var isMobileOpen = function () {
        return Boolean(mobileMenu && !mobileMenu.hidden);
      };
      var finishMobileClose = function (opts) {
        opts = opts || {};
        clearTimer(mobileTimer);
        mobileTimer = 0;
        if (!mobileMenu) return;
        mobileMenu.dataset.state = 'closed';
        mobileMenu.hidden = true;
        if (scrollLocked) {
          document.documentElement.removeAttribute('data-ej-scroll-lock');
          scrollLocked = false;
        }
        Array.prototype.forEach.call(root.querySelectorAll('[data-ej-untab]'), function (c) {
          var prev = c.dataset.ejUntab;
          if (prev) c.setAttribute('tabindex', prev);
          else c.removeAttribute('tabindex');
          delete c.dataset.ejUntab;
        });
        subpanels.forEach(function (sp) {
          closeSubpanel(sp, { restoreFocus: false });
        });
        if (opts.restoreFocus !== false && mobileOpener && mobileOpener.isConnected) {
          mobileOpener.focus({ preventScroll: true });
        }
      };
      var openMobile = function (opener) {
        if (!mobileMenu || isMobileOpen()) return;
        if (mobileSection && getComputedStyle(mobileSection).display === 'none') return;
        clearTimer(mobileTimer);
        mobileOpener = opener instanceof HTMLElement ? opener : mobileTrigger;
        Array.prototype.forEach.call(
          root.querySelectorAll('[data-mobile-site-header] a[href], [data-mobile-site-header] button'),
          function (c) {
            if (c.hasAttribute('data-close-mobile-menu') || c.hasAttribute('data-back-subpanel')) return;
            c.dataset.ejUntab = c.getAttribute('tabindex') || '';
            c.setAttribute('tabindex', '-1');
          }
        );
        if (!document.documentElement.hasAttribute('data-ej-scroll-lock')) {
          document.documentElement.setAttribute('data-ej-scroll-lock', '');
          scrollLocked = true;
        }
        positionMobileMenu();
        requestAnimationFrame(positionMobileMenu);
        var wasHidden = mobileMenu.hidden;
        mobileMenu.hidden = false;
        mobileMenu.inert = false;
        setMenuState('level1');
        if (mobileTrigger) mobileTrigger.setAttribute('aria-expanded', 'true');
        if (motionDisabled()) {
          mobileMenu.dataset.state = 'open';
        } else {
          if (wasHidden) {
            mobileMenu.dataset.state = 'closed';
            mobileMenu.getBoundingClientRect();
          }
          mobileMenu.dataset.state = 'opening';
          mobileTimer = later(function () {
            if (mobileMenu.dataset.state === 'opening') mobileMenu.dataset.state = 'open';
            mobileTimer = 0;
          }, MOBILE_MOTION);
        }
        later(function () {
          var close = root.querySelector('[data-when~="level1"][data-close-mobile-menu]');
          if (close) close.focus({ preventScroll: true });
        }, 160);
      };
      var closeMobile = function (opts) {
        opts = opts || {};
        if (!isMobileOpen()) return;
        clearTimer(mobileTimer);
        mobileMenu.inert = true;
        setMenuState('closed');
        if (mobileTrigger) mobileTrigger.setAttribute('aria-expanded', 'false');
        if (motionDisabled()) {
          finishMobileClose(opts);
          return;
        }
        mobileMenu.dataset.state = 'closing';
        mobileTimer = later(function () {
          finishMobileClose(opts);
        }, MOBILE_MOTION);
      };
      Array.prototype.forEach.call(root.querySelectorAll('[data-open-mobile-menu]'), function (b) {
        listen(b, 'click', function () {
          openMobile(b);
        });
      });
      Array.prototype.forEach.call(root.querySelectorAll('[data-close-mobile-menu]'), function (b) {
        listen(b, 'click', function () {
          closeMobile();
        });
      });
      listen(mobileSection, 'keydown', function (e) {
        if (!isMobileOpen()) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          var sub = openSub();
          if (sub) {
            closeSubpanel(sub);
            return;
          }
          var openAcc = accordions.find(accExpanded);
          if (openAcc) setAcc(openAcc, false);
          else closeMobile();
          return;
        }
        if (e.key !== 'Tab') return;
        var cs = Array.prototype.filter.call(
          mobileSection.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])'),
          function (c) {
            return (
              c.getClientRects().length > 0 &&
              !c.closest('[inert]') &&
              getComputedStyle(c).visibility !== 'hidden' &&
              c.getAttribute('tabindex') !== '-1' &&
              (mobileMenu.contains(c) ||
                c.hasAttribute('data-close-mobile-menu') ||
                c.hasAttribute('data-back-subpanel'))
            );
          }
        );
        var first = cs[0];
        var last = cs[cs.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          if (last) last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          if (first) first.focus();
        }
      });
      listen(window, 'resize', function () {
        if (isMobileOpen()) positionMobileMenu();
        scheduleArcs();
      });

      /* ---------- responsive layout switch (Liquid-computed) ---------- */
      var clearPredictiveResults = function (wrap) {
        if (!wrap) return;
        Array.prototype.forEach.call(
          wrap.querySelectorAll('[data-predictive-search-target]'),
          function (el) {
            el.innerHTML = '';
            el.style.display = 'none';
          }
        );
      };
      var setLayout = function (layout) {
        if (root.dataset.layout === layout) return;
        root.dataset.layout = layout;
        if (layout === 'mobile') {
          closeMega();
          closeSearch();
          clearPredictiveResults(desktopWrap);
        } else {
          closeMobile({ restoreFocus: false });
          clearPredictiveResults(mobileSection);
        }
      };
      var desktopMinWidth = parseInt(root.dataset.desktopMinWidth, 10);
      if (!(desktopMinWidth > 0)) desktopMinWidth = 100000;
      var desktopQuery = window.matchMedia('(min-width: ' + desktopMinWidth + 'px)');
      var syncLayout = function () {
        setLayout(desktopQuery.matches ? 'desktop' : 'mobile');
        scheduleArcs();
      };
      this._arcFrameCancel = function () {
        cancelAnimationFrame(arc.frame);
        cancelAnimationFrame(arc.revealFrame);
        cancelAnchor();
      };
      listen(desktopQuery, 'change', syncLayout);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleArcs);
      syncLayout();

      /* ---------- Theme Editor lifecycle ---------- */
      if (window.Shopify && window.Shopify.designMode) {
        listen(document, 'shopify:block:select', function (e) {
          if (!root.contains(e.target)) return;
          var panel = e.target.closest('[data-menu-panel]');
          if (panel) {
            var trigger = megaTriggers.find(function (t) {
              return t.getAttribute('data-mega-trigger') === panel.id;
            });
            if (trigger) openMega(trigger, { source: 'programmatic' });
            return;
          }
          var acc = e.target.closest('[data-mobile-accordion]');
          if (acc && root.dataset.layout === 'mobile') {
            openMobile(mobileTrigger);
            setAcc(acc, true);
            return;
          }
          var sub = e.target.closest('[data-subpanel]');
          if (sub && root.dataset.layout === 'mobile') {
            openMobile(mobileTrigger);
            openSubpanel(sub.id, null);
            return;
          }
          if (e.target.closest('[data-mobile-menu]') && root.dataset.layout === 'mobile') {
            openMobile(mobileTrigger);
          }
        });
        listen(document, 'shopify:block:deselect', function (e) {
          if (!root.contains(e.target)) return;
          if (e.target.closest('[data-menu-panel]')) closeMega();
          if (e.target.closest('[data-mobile-menu]')) closeMobile({ restoreFocus: false });
        });
      }
    }

    disconnectedCallback() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      if (this._timers) {
        this._timers.forEach(function (t) {
          clearTimeout(t);
        });
        this._timers.clear();
      }
      if (this._arcFrameCancel) this._arcFrameCancel();
      if (document.documentElement.hasAttribute('data-ej-scroll-lock')) {
        document.documentElement.removeAttribute('data-ej-scroll-lock');
      }
    }
  }

  customElements.define('ej-header', EJHeader);
})();
